import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Formik } from 'formik';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import Button from 'app/components/Button';
import Popup from 'app/components/Popup';
import Textarea from 'app/components/Textarea';
import Spinner from 'app/components/Spinner';
import { Select } from 'app/components/Select';

import 'app/modules/Orders/components/Modals/CancelOrder/CancelOrder.scss';
import { singleOrderActions } from 'app/api/singleOrderActions';

export default function CancelOrder({
  modalDeleteData = {
    show: false,
    orderId: null
  },
  setModalDeleteData = null,
  handleUpdateOrdersList,
  reloadData = false
}) {
  const [loading, setLoading] = useState(false);
  const popupBody = useRef(null);
  const { show, orderId } = modalDeleteData;
  const intl = useIntl();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const minHeight = useMemo(() => popupBody?.current?.clientHeight, [loading]);

  const cancellationReasonTypeOptions = useMemo(
    () => [
      {
        value: 1,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.quantity_or_sku_modification'
        })
      },
      {
        value: 2,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.duplicated_order'
        })
      },
      {
        value: 3,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.wrong_order'
        })
      },
      {
        value: 4,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.end_customer_cancellation'
        })
      },
      {
        value: 5,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.change_delivery_address'
        })
      },
      {
        value: 6,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.negative_margin'
        })
      },
      {
        value: 7,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.carrier_high_cost'
        })
      },
      {
        value: 8,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.wrong_ean_marketplace'
        })
      },
      {
        value: 9,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.end_customer_fraud'
        })
      },
      {
        value: 10,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.test_order'
        })
      },
      {
        value: 0,
        label: intl.formatMessage({
          id: 'controlpanel.orders.modal_delete.cancellation_reason.other_reason'
        })
      }
    ],
    [intl]
  );

  const onCancel = (resetForm) => {
    resetForm();
    setModalDeleteData({
      show: false,
      orderId: null
    });
  };

  const onSubmit = async (values, { resetForm }) => {
    try {
      const request = await singleOrderActions({
        id: orderId,
        type: 'cancel-order',
        message: {
          cancellationReasonType: values?.cancellationReasonType,
          cancellationReason:
            values?.cancellationReasonType === 0
              ? values?.cancellationReason
              : null
        },
        setLoading,
        customSuccessFeedback: {
          variant: 'success',
          content: intl.formatMessage({
            id: 'controlpanel.global.order_cancell'
          }),
          autoClose: true,
          dismissible: false
        }
      });

      if (request) {
        handleUpdateOrdersList();
        onCancel(resetForm);
      }

      if (reloadData) {
        window.location.reload();
      }
    } catch (error) {
      onCancel(resetForm);
    }
  };

  return (
    <Formik
      initialValues={{
        cancellationReasonType: null,
        cancellationReason: null
      }}
      validationSchema={Yup.object().shape({
        cancellationReasonType: Yup.number().required(
          intl.formatMessage({ id: 'controlpanel.form.validation.required' })
        ),
        cancellationReason: Yup.string()
          .nullable()
          .when('cancellationReasonType', (cancellationReasonType, schema) => {
            if (
              cancellationReasonType[0] === 0 ||
              cancellationReasonType[0] === 8
            ) {
              return schema.required(
                intl.formatMessage({
                  id: 'controlpanel.form.validation.required'
                })
              );
            }
            return schema;
          })
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { resetForm }) => {
        // eslint-disable-next-line no-console
        await onSubmit(values, { resetForm });
        return false;
      }}
    >
      {(formik) => {
        const showTextarea =
          formik.values.cancellationReasonType === 0 ||
          formik.values.cancellationReasonType === 8;

        const textareaLabel =
          formik.values.cancellationReasonType === 8
            ? intl.formatMessage({
                id: 'controlpanel.orders.modal_delete.ean.label'
              })
            : intl.formatMessage({
                id: 'controlpanel.orders.modal_delete.label'
              });
        return (
          <Popup
            show={show}
            header={
              <div className="bb-popup-cancelorder__header">
                <p className="bold">
                  {intl.formatMessage({ id: 'controlpanel.orders.cancel' })}
                </p>
              </div>
            }
            body={
              loading ? (
                <Spinner containerHeight={minHeight || 0} size="4x" />
              ) : (
                <div className="bb-popup-cancelorder__body" ref={popupBody}>
                  <p className="bb-popup-cancelorder__body__question">
                    {intl.formatMessage(
                      {
                        id: 'controlpanel.orders.modal_delete.confirmation_msg'
                      },
                      {
                        id: orderId
                      }
                    )}
                  </p>
                  <Select
                    name="cancellationReasonType"
                    id="cancellationReasonType"
                    placeholder={intl.formatMessage({
                      id: 'controlpanel.orders.modal_delete.cancellation_reason.placeholder'
                    })}
                    options={cancellationReasonTypeOptions}
                  />
                  {showTextarea && (
                    <>
                      <p className="bb-popup-cancelorder__body__explain">
                        {intl.formatMessage({
                          id: textareaLabel
                        })}
                      </p>
                      <Textarea
                        formik={formik}
                        name="cancellationReason"
                        maxLength={255}
                      />
                    </>
                  )}
                </div>
              )
            }
            footer={
              <div className="bb-popup-cancelorder__footer">
                <Button
                  text={intl.formatMessage({
                    id: 'controlpanel.global.cancel'
                  })}
                  type="default"
                  size="big"
                  typeButton="button"
                  onClick={() => onCancel(formik.resetForm)}
                  disabled={loading}
                />
                <Button
                  text={intl.formatMessage({
                    id: 'controlpanel.orders.cancel'
                  })}
                  type="primary"
                  size="big"
                  typeButton="submit"
                  onClick={formik.handleSubmit}
                  disabled={loading}
                />
              </div>
            }
            handleClose={() => onCancel(formik.resetForm)}
          />
        );
      }}
    </Formik>
  );
}

CancelOrder.propTypes = {
  modalDeleteData: PropTypes.shape({
    show: PropTypes.bool,
    orderId: PropTypes.string
  }),
  setModalDeleteData: PropTypes.func,
  handleUpdateOrdersList: PropTypes.func.isRequired,
  reloadData: PropTypes.bool
};
